import { Fragment, useState } from 'react';
import PaymentStatusBadge from './PaymentStatusBadge';
import StatusBadge from './StatusBadge';
import OrderDetailsPanel from './OrderDetailsPanel';
import { formatPKR } from '../api';
import { formatOrderDate, formatOrderTime } from '../utils/orderUtils';

/**
 * Dedicated table for online payment orders awaiting verification.
 * Shows Verify Payment and Reject Payment buttons inline on each row.
 * No fulfillment actions here — those belong in Active Orders.
 */
export default function PendingVerificationTable({
  orders,
  onVerify,
  onReject,
  actionLoading,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const COL_COUNT = 9; // 8 data cols + 1 actions col

  if (!orders.length) {
    return (
      <div className="glass-card text-center text-lumora-text/50 py-12">
        No orders awaiting payment verification
      </div>
    );
  }

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Phone</th>
            <th>Products</th>
            <th>Amount</th>
            <th>Payment</th>
            <th>Payment Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const expanded  = expandedId === order.orderId;
            const isLoading = actionLoading === order.orderId;
            const isRejected = order.paymentStatus === 'Rejected';

            return (
              <Fragment key={order.orderId}>
                <tr className={expanded ? 'bg-lumora-secondary/20' : undefined}>
                  {/* Order ID — expandable */}
                  <td className="font-mono">
                    <button
                      type="button"
                      onClick={() => toggle(order.orderId)}
                      className="inline-flex items-center gap-2 text-left hover:text-lumora-wood transition-colors"
                      aria-expanded={expanded}
                    >
                      <span
                        className={`inline-block text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}
                        aria-hidden
                      >
                        ▶
                      </span>
                      #{order.orderId}
                    </button>
                  </td>

                  <td>
                    <div>{formatOrderDate(order.date)}</div>
                    <div className="text-xs text-lumora-text/50">{formatOrderTime(order.time)}</div>
                  </td>

                  <td>{order.customerName}</td>
                  <td className="font-mono text-sm">{order.customerPhone}</td>

                  {/* Products */}
                  <td>
                    {order.items?.map((item, i) => (
                      <div key={i} className="text-sm">
                        {item.productName} × {item.quantity}
                      </div>
                    ))}
                  </td>

                  <td className="font-medium">{formatPKR(order.orderTotal)}</td>

                  {/* Payment method — show NayaPay / JazzCash */}
                  <td>
                    <span className="font-medium text-lumora-dark-wood">
                      {order.paymentType || order.paymentMethod || '—'}
                    </span>
                  </td>

                  {/* Payment status badge */}
                  <td>
                    <PaymentStatusBadge status={order.paymentStatus} />
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="flex flex-row gap-1.5 items-center">
                      {/* Always show Verify — even for Rejected so admin can re-verify */}
                      <button
                        type="button"
                        disabled={isLoading}
                        className="btn-primary text-xs whitespace-nowrap px-2.5 py-1"
                        onClick={() => onVerify(order.orderId)}
                      >
                        {isLoading ? '…' : 'Verify'}
                      </button>

                      {/* Only show Reject when status is Pending Verification */}
                      {!isRejected && (
                        <button
                          type="button"
                          disabled={isLoading}
                          className="btn-secondary text-xs whitespace-nowrap px-2.5 py-1 text-red-700 border-red-200 hover:border-red-300"
                          onClick={() => onReject(order.orderId)}
                        >
                          Reject
                        </button>
                      )}

                      {isRejected && (
                        <span className="text-xs text-red-600 font-medium whitespace-nowrap">
                          Rejected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded order details row */}
                {expanded && (
                  <tr>
                    <td colSpan={COL_COUNT} className="!py-4 !px-4">
                      <OrderDetailsPanel order={order} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
